import type { ReactNode } from 'react'
import clsx from 'clsx'
import { InfoCircle, ShieldCheck } from '@/assets/icons'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/popover/Popover'
import { SupportedFormatsContent } from '@/user_files/drive/components/supported-formats/SupportedFormatsContent'
import { brandedAIName } from '@/util/enterprise'

export type BottomStickyBarItemProps = {
  /** Trigger control (e.g. icon with `aria-hidden`) */
  icon: ReactNode
  /** Trigger label. */
  label: string
  /** Popover body */
  children: ReactNode
  /** Popover alignment offset. */
  alignOffset?: number
  /** Popover content class name. */
  contentClassName?: string
}

/** One trigger + popover pair for the Drive bottom sticky bar. */
export function BottomStickyBarItem({ icon, label, children, alignOffset, contentClassName }: BottomStickyBarItemProps) {
  return (
    <Popover>
      <PopoverTrigger
        className={clsx(
          'inline-flex items-center gap-1 rounded px-2 py-0.5 text-xs font-medium text-brand-neutral-600',
          'outline-none hover:bg-brand-neutral-100 focus-visible:ring-2 focus-visible:ring-brand-500',
          'data-[state=open]:bg-brand-neutral-100'
        )}
        type="button"
      >
        {icon}
        {label}
      </PopoverTrigger>
      <PopoverContent side="top" sideOffset={8} width="auto" showCloseButton={false} align="end" alignOffset={alignOffset} className={contentClassName}>
        <div className="text-sm leading-5 tracking-[-0.15px] text-brand-neutral-950">{children}</div>
      </PopoverContent>
    </Popover>
  )
}

export function BottomStickyBar() {
  return (
    <div
      className={clsx(
        'pointer-events-auto fixed bottom-3 right-10 z-[998]',
        'flex items-center gap-1 rounded border border-brand-neutral-200 bg-brand-neutral-50 p-0.5',
        'shadow-[0px_0px_15px_0px_rgba(0,0,0,0.1),0px_0px_6px_0px_rgba(0,0,0,0.05)]'
      )}
      role="region"
      aria-label="Drive information"
    >
      <BottomStickyBarItem
        alignOffset={-80} // forcing right alignment with edge of BottomStickyBar
        contentClassName="max-w-[min(385px,calc(100vw-3rem))]"
        icon={<InfoCircle className="h-3.5 w-3.5 shrink-0 text-brand-neutral-600" aria-hidden />}
        label="Supported Formats"
      >
        <SupportedFormatsContent />
      </BottomStickyBarItem>

      <div className="h-4 w-px shrink-0 bg-brand-neutral-300" aria-hidden />

      <BottomStickyBarItem
        contentClassName="max-w-[min(282px,calc(100vw-3rem))]"
        icon={<ShieldCheck className="h-3.5 w-3.5 shrink-0 text-brand-neutral-600" aria-hidden />}
        label="HIPAA"
      >
        <p>
          {brandedAIName} rigorously adheres to SOC 2, ISO 27001, and HIPAA standards. Your documents and data are secure, and never used to train{' '}
          {brandedAIName}.
        </p>
      </BottomStickyBarItem>
    </div>
  )
}
